import React, { useState } from "react";
import { useHints, hintText } from "../../hints/HintContext";
import { SOURCE, SOURCE_LABEL } from "../../hints/progress";
import { formatTime } from "../../utils/format";
import "./HintPanel.css";

// HintPanel — the GM's hint workflow (SCRUM-686).
//
// Lives inside the chat bubble, right above the message box. One thing is in focus: the next
// hint for the riddle the team is on, with a "Brug" button that puts it in the chat box (the GM
// edits and sends there, so a hint never leaves without a human sending it). Everything else is
// one compact line: where the team is (and a way to move the guess), how sure we are, and the
// language. The other levels and the room's situational hints unfold only on request.
//
// The GM is never asked to tick anything off. Moving to another riddle is a signal, not a task.

const LEVEL_LABEL = { 1: "Lille skub", 2: "Mere hjælp", 3: "Løsning" };

// Hints longer than this are clipped to a few lines until the GM taps them. Two HUDs side by
// side leave the chat ~half as wide, where a five-line hint becomes twelve and pushes the
// message box off the bottom.
const CLAMP_CHARS = 140;
const FOLD_KEY = "hintPanelFolded";

export default function HintPanel() {
    const hints = useHints();
    const [showAll, setShowAll] = useState(false);
    const [showSitu, setShowSitu] = useState(false);
    // Folded = only the top line (where the team is) stays; the GM unfolds when a hint is
    // wanted. Remembered per browser, so a GM running two rooms keeps the chat usable.
    const [folded, setFolded] = useState(() => {
        try { return localStorage.getItem(FOLD_KEY) === "1"; } catch { return false; }
    });
    const toggleFolded = () => setFolded(f => {
        try { localStorage.setItem(FOLD_KEY, f ? "0" : "1"); } catch { /* private mode */ }
        return !f;
    });
    const [openText, setOpenText] = useState(null); // "riddleId:level" shown in full

    if (!hints || !hints.bank || !hints.progress) return null;

    const { progress, elapsedSec, secondsOnCurrent, draft, pickHint, override, setOverride, demo, bank, language, setLanguage, languageFromController } = hints;
    const { items, currentIndex, source, minutesBehind } = progress;
    const current = currentIndex >= 0 ? items[currentIndex] : null;
    const situational = Array.isArray(bank.situational) ? bank.situational.filter(s => s && s.text) : [];

    const minutesOn = Math.floor(secondsOnCurrent / 60);
    const stuck = current && (current.expectedMin || 0) > 0 && minutesOn >= current.expectedMin;
    const confidence = source === SOURCE.sensor ? "high" : source === SOURCE.none ? "none" : "low";

    const jumpTo = (index) => {
        if (index < 0 || index >= items.length) return;
        setOverride(index === progress.currentIndex && !override ? null : items[index].id);
    };

    const nextHint = current ? current.hints.find(h => h.level === current.nextLevel) || current.hints[current.hints.length - 1] : null;
    const otherHints = current ? current.hints.filter(h => h !== nextHint) : [];

    const renderCard = (h, primary) => {
        const sent = current.sentLevels.includes(h.level);
        const isDraft = draft && draft.riddleId === current.id && draft.level === h.level;
        const key = `${current.id}:${h.level}`;
        const text = hintText(h, language);
        const long = text.length > CLAMP_CHARS;
        const open = openText === key;
        return (
            <div key={h.level} className={`hint-card ${sent ? "is-sent" : ""} ${primary ? "is-next" : ""} ${isDraft ? "is-draft" : ""}`}>
                <span className={`hint-level hint-level-${h.level}`} title={LEVEL_LABEL[h.level] || `Niveau ${h.level}`}>{h.level}</span>
                <span className="hint-card-body">
                    <span className={`hint-card-text ${long && !open ? "clamped" : ""}`}>
                        {text}
                        {sent && <span className="hint-sent-mark"> · sendt {sentAtLabel(hints.sentHints, current.id, h.level)}</span>}
                    </span>
                    {long && (
                        <button className="hint-link hint-more" onClick={() => setOpenText(open ? null : key)}>
                            {open ? "mindre" : "hele teksten"}
                        </button>
                    )}
                </span>
                <button className={`hint-use-btn ${primary ? "primary" : ""}`} onClick={() => pickHint(current, h)} disabled={!!isDraft}>
                    {isDraft ? "I feltet" : sent ? "Igen" : "Brug"}
                </button>
            </div>
        );
    };

    return (
        <div className={`hint-panel ${stuck ? "stuck" : ""} ${folded ? "folded" : ""}`}>
            {/* Where the team is: ‹ [1/11 · Sengen ▾] › + how we know + language + fold. */}
            <div className="hint-top">
                <button className="hint-nav" onClick={() => jumpTo(currentIndex - 1)} disabled={currentIndex <= 0} title="Forrige gåde">‹</button>
                <select
                    className="hint-select"
                    value={currentIndex >= 0 ? currentIndex : ""}
                    onChange={e => jumpTo(Number(e.target.value))}
                    title="Hvilken gåde holdet er på. Vælg en anden, hvis gættet er forkert."
                >
                    {items.map(r => (
                        <option key={r.id} value={r.index}>
                            {r.index + 1}/{items.length} · {r.title}{r.status === "solved" ? " ✓" : r.status === "probably_solved" ? " (✓)" : ""}{r.sentLevels.length ? ` · ${r.sentLevels.length} sendt` : ""}
                        </option>
                    ))}
                </select>
                <button className="hint-nav" onClick={() => jumpTo(currentIndex + 1)} disabled={currentIndex >= items.length - 1} title="Næste gåde">›</button>

                <span className="hint-status" title={SOURCE_LABEL[source]}>
                    <span className={`hint-conf hint-conf-${confidence}`} />
                    {override ? (
                        <button className="hint-link" onClick={() => setOverride(null)} title="Tilbage til systemets gæt">valgt af dig ↺</button>
                    ) : (
                        <span className={`hint-conf-text-${confidence}`}>{SOURCE_LABEL[source]}</span>
                    )}
                    {elapsedSec != null && current && (
                        <span className="hint-pace">
                            · {minutesOn} min (plejer {current.expectedMin})
                            {minutesBehind > 2 && <span className="hint-pace-behind"> · {minutesBehind} min bagud</span>}
                            {minutesBehind < -2 && <span className="hint-pace-ahead"> · {-minutesBehind} min foran</span>}
                        </span>
                    )}
                </span>

                <span className="hint-lang" title={languageFromController ? "Sproget fra spilstarten. Skift her sender også til rummet." : "Hintenes sprog"}>
                    {["da", "en"].map(l => (
                        <button key={l} className={`hint-lang-btn ${language === l ? "active" : ""}`} onClick={() => setLanguage(l)}>{l.toUpperCase()}</button>
                    ))}
                </span>
                <button className="hint-nav hint-fold" onClick={toggleFolded} title={folded ? "Vis hints" : "Fold hints sammen (kun linjen her bliver)"}>
                    {folded ? "▸" : "▾"}
                </button>
            </div>

            {folded ? null : <>
            {stuck && (
                <div className="hint-nudge">
                    Længere tid end normalt på {current.title.toLowerCase()}.{current.nextLevel ? ` Overvej hint ${current.nextLevel}.` : " Alle hints er sendt."}
                </div>
            )}

            {current && nextHint ? (
                <div className="hint-list">
                    {renderCard(nextHint, true)}
                    {showAll && otherHints.map(h => renderCard(h, false))}
                </div>
            ) : (
                <div className="hint-empty">Ingen hints til denne gåde endnu.</div>
            )}

            {/* Footer: the other levels, and the room's situational hints as a row of chips. */}
            <div className="hint-foot">
                {current && current.hints.length > 1 && (
                    <button className="hint-link" onClick={() => setShowAll(s => !s)}>
                        {showAll ? "kun næste" : `alle ${current.hints.length} niveauer`}
                    </button>
                )}
                {situational.length > 0 && (
                    <button className={`hint-link ${showSitu ? "is-on" : ""}`} onClick={() => setShowSitu(s => !s)}>
                        situationshints {showSitu ? "▴" : "▾"}
                    </button>
                )}
                {demo && (
                    <span className="hint-demo">
                        <span className="hint-demo-label">demo</span>
                        <button onClick={() => demo.addMinutes(5)}>+5 min</button>
                        <button onClick={() => demo.solveNext()}>prop løst</button>
                        <button onClick={() => demo.fakePlayerMessage(randomPlayerLine(bank))}>spiller skriver</button>
                        <button onClick={() => demo.reset()}>nulstil</button>
                    </span>
                )}
            </div>
            {showSitu && (
                <div className="hint-situ">
                    {situational.map((s, i) => {
                        const id = `situation:${i}`;
                        const isDraft = draft && draft.riddleId === id;
                        return (
                            <button
                                key={id}
                                className={`hint-situ-chip ${isDraft ? "is-draft" : ""}`}
                                title={hintText(s, language)}
                                onClick={() => pickHint({ id, title: s.label }, { level: 0, text: s.text, en: s.en })}
                            >
                                {s.label}
                            </button>
                        );
                    })}
                </div>
            )}
            </>}
        </div>
    );
}

// HintHeaderSummary is the short "4/7 · Tastaturet ●" the chat header shows, folded or not.
export function HintHeaderSummary() {
    const hints = useHints();
    if (!hints?.progress) return null;
    const { items, currentIndex, source } = hints.progress;
    if (currentIndex < 0) return null;
    const confidence = source === SOURCE.sensor ? "high" : "low";
    return (
        <span className="hint-header-summary" title={SOURCE_LABEL[source]}>
            <span className={`hint-conf hint-conf-${confidence}`} />
            {currentIndex + 1}/{items.length} · {items[currentIndex].title}
        </span>
    );
}

function sentAtLabel(sentHints, riddleId, level) {
    const h = [...sentHints].reverse().find(s => s.riddleId === riddleId && s.level === level);
    return h && h.elapsedSec != null ? formatTime(h.elapsedSec) : "";
}

function randomPlayerLine(bank) {
    const r = bank.riddles[Math.floor(Math.random() * bank.riddles.length)];
    return `Hvad skal vi gøre med ${r.keywords[0] || r.title.toLowerCase()}?`;
}
